export const exhaustiveCheck = (_param: never): never => {
  throw new Error(`exhaustive check [${String(_param as unknown)}] not handled`)
}

/**
 * Same as Object.keys() but with better typing
 * */
export function keys<T extends object>(o: T): Array<keyof T> {
  return Object.keys(o) as Array<keyof T>
}

/**
 * Not nullish typeguard
 * */
export function isNotNullish<T>(value: T | null | undefined): value is T {
  return value != null
}

type ObjectEntry<BaseType> = [keyof BaseType, BaseType[keyof BaseType]]
/**
 * Same as Object.entries() but with better typing
 * */
export function entries<T extends object>(obj: T): Array<ObjectEntry<T>> {
  return Object.entries(obj) as Array<ObjectEntry<T>>
}

/**
 * Same as Array.includes() but with better typing
 * */
export function includes<T extends U, U>(coll: ReadonlyArray<T>, el: U): el is T {
  return coll.includes(el as T)
}

/**
 * Same as Object.fromEntries() but with better typing
 * */
export function fromEntries<K extends string, T>(entries: Iterable<readonly [K, T]>): { [k in K]: T } {
  return Object.fromEntries(entries) as { [k in K]: T }
}

/**
 * Recursively strips the nominal `__chapito` brand from leaf id types so typia
 * can synthesise a runtime validator ({@link ActionTarget} for example).
 *
 * The brand is a compile-time-only fiction — at runtime a branded id such as
 * `GroupId` (`string & { __chapito: 'groupId' }`) is an ordinary `string` — so
 * validating the unbranded shape is exactly equivalent at runtime. This keeps
 * `grab/types.ts` untouched: the branded `ActionTarget` stays the public type
 * and this transform exists only to feed typia, which rejects the
 * `string & {...}` intersections as "nonsensible".
 */
export type Unbranded<T> = T extends string & { __chapito: unknown }
  ? string
  : T extends number & { __chapito: unknown }
  ? number
  : // eslint-disable-next-line @typescript-eslint/ban-types
  T extends Date | Function | RegExp
  ? T
  : T extends readonly (infer U)[]
  ? Array<Unbranded<U>>
  : T extends object
  ? { [K in keyof T]: Unbranded<T[K]> }
  : T
